import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;

const CAL_STATUS = {
  overdue:  { label:'Overdue',         color:'#EC2124' },
  never:    { label:'Never Calibrated',color:'#94a3b8' },
  'due-soon':{ label:'Due Soon',       color:'#f59e0b' },
  ok:       { label:'Up to Date',      color:'#46BB89' },
};

export default function CalibrationDashboard() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/equipment/calibration-overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) setItems(data.items || []);
    } catch {} finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const counts = items.reduce((acc, i) => { acc[i.calibrationStatus] = (acc[i.calibrationStatus]||0)+1; return acc; }, {});
  const visible = filter ? items.filter(i => i.calibrationStatus === filter) : items;

  const fs = { background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)', borderRadius:6, padding:'6px 10px', fontSize:13 };

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800 }}>Calibration Dashboard</h2>
        <button onClick={load} style={{ background:'none', border:'1px solid var(--ni-border-soft)',
          color:'var(--ni-text-muted)', borderRadius:6, padding:'6px 12px', fontSize:12, cursor:'pointer' }}>
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        {Object.entries(CAL_STATUS).map(([key, { label, color }]) => (
          <div key={key} onClick={() => setFilter(f => f===key?'':key)}
            style={{ background:'var(--ni-panel)', border:`1px solid ${filter===key?color:'var(--ni-border-soft)'}`,
              borderRadius:10, padding:'14px 16px', cursor:'pointer',
              boxShadow: filter===key?`0 0 0 1px ${color}33`:undefined }}>
            <div style={{ fontSize:22, fontWeight:800, color }}>{counts[key]||0}</div>
            <div style={{ fontSize:12, color:'var(--ni-text-muted)', marginTop:2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <select value={filter} onChange={e=>setFilter(e.target.value)} style={fs}>
          <option value="">All ({items.length})</option>
          {Object.entries(CAL_STATUS).map(([k,{label}])=>(
            <option key={k} value={k}>{label} ({counts[k]||0})</option>
          ))}
        </select>
      </div>

      <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', borderRadius:10, overflow:'hidden' }}>
        {loading ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--ni-text-muted)' }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--ni-text-muted)' }}>
            {items.length === 0 ? 'No calibration-required equipment registered.' : 'No items match this filter.'}
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ color:'var(--ni-text-muted)', fontSize:11, textTransform:'uppercase',
                letterSpacing:'0.06em', background:'rgba(0,0,0,0.2)' }}>
                {['Doc No.','Name','Type','Last Calibrated','Next Due','Status'].map(h=>(
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(item => {
                const { color, label } = CAL_STATUS[item.calibrationStatus] || {};
                return (
                  <tr key={item._id} onClick={() => navigate(`/quality/equipment/${item._id}`)}
                    style={{ cursor:'pointer', borderTop:'1px solid var(--ni-border-soft)' }}
                    onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.03)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'11px 16px', fontFamily:'monospace', fontSize:12, color:'#f59e0b', whiteSpace:'nowrap' }}>{item.documentNumber}</td>
                    <td style={{ padding:'11px 16px', fontWeight:600 }}>{item.name}</td>
                    <td style={{ padding:'11px 16px', color:'var(--ni-text-muted)' }}>{item.type}</td>
                    <td style={{ padding:'11px 16px', color:'var(--ni-text-muted)', whiteSpace:'nowrap' }}>
                      {item.lastCalibratedAt ? new Date(item.lastCalibratedAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding:'11px 16px', whiteSpace:'nowrap',
                      color: item.calibrationStatus==='overdue'?'#EC2124':item.calibrationStatus==='due-soon'?'#f59e0b':'var(--ni-text-muted)',
                      fontWeight: item.calibrationStatus==='overdue'?700:400 }}>
                      {item.nextCalibrationDue ? new Date(item.nextCalibrationDue).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding:'11px 16px' }}>
                      <span style={{ fontSize:11, fontWeight:700, color, background:color+'22',
                        borderRadius:4, padding:'2px 8px', letterSpacing:'0.04em', textTransform:'uppercase' }}>
                        {label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ marginTop:10, color:'var(--ni-text-muted)', fontSize:12 }}>
        {visible.length} of {items.length} item{items.length!==1?'s':''} requiring calibration
      </div>
    </div>
  );
}
